import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@base-ui/react/popover';
import { DayPicker } from 'react-day-picker';
import { ja } from 'react-day-picker/locale';
import 'react-day-picker/style.css';
import { Button } from '@components/ui/Button/Button';
import { ClickableDay } from '@components/ui/DatePicker/ClickableDay';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { localDateToYmd, ymdToLocalDate } from '@components/Pages/AnalyticsPage/utils/dateRules';
import { ChevronLeftIcon, ChevronRightIcon } from '@utils/icons';
import { formatDateRange } from '../model/formatDateRange';
import { PLAN_WEEKS, type IPlanWindow, type PlanWeeks } from '../WorkPlan/workPlanModel';
import styles from './dateNavigator.module.css';

export interface IDateNavigatorProps {
  window: IPlanWindow;
  today: string;
  weeks: PlanWeeks;
  /** Page by whole weeks: -1 back, +1 forward. */
  onPage: (weeks: number) => void;
  /** Show the week holding this day (`YYYY-MM-DD`). */
  onJump: (day: string) => void;
  onWeeksChange: (weeks: PlanWeeks) => void;
}

/**
 * The page's date picker, as Trusstor's calendar bar draws it: the window's
 * dates boxed between the week steps. The dates open a calendar —
 * the window's length (1 / 2 / 4 weeks) over a month with the window marked;
 * picking a day shows its week. Every view of the page reads the window.
 *
 * The calendar is tz-naive like every picker here: it works on local Y/M/D
 * and the window's `YYYY-MM-DD` site days round-trip through them.
 */
export function DateNavigator({ window, today, weeks, onPage, onJump, onWeeksChange }: IDateNavigatorProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const from = ymdToLocalDate(window.from);
  const calendarLocale = i18n.language === 'ja' ? ja : undefined;
  return (
    <div className={styles.range}>
      <Button variant="ghost" size="sm" className={styles.step} aria-label={t('activities.workPlan.prev')} onClick={() => onPage(-1)}>
        <ChevronLeftIcon size={14} />
      </Button>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger type="button" className={styles.rangeText} aria-label={t('activities.workPlan.pickDates')}>
          {formatDateRange(window.from, window.to, i18n.language, Number(today.slice(0, 4)))}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner className={styles.positioner} sideOffset={6} align="center" collisionPadding={8}>
            <Popover.Popup className={styles.popup}>
              <div className={styles.length}>
                <SegmentedControl<`${PlanWeeks}`>
                  variant="outlined"
                  value={`${weeks}`}
                  onChange={(next) => onWeeksChange(Number(next) as PlanWeeks)}
                  ariaLabel={t('activities.workPlan.window.label')}
                  options={PLAN_WEEKS.map((option) => ({ id: `${option}`, label: t(`activities.workPlan.window.${option}`) }))}
                />
              </div>
              <DayPicker
                mode="range"
                selected={{ from, to: ymdToLocalDate(window.to) }}
                defaultMonth={from}
                weekStartsOn={1}
                locale={calendarLocale}
                onSelect={(_range, day) => {
                  onJump(localDateToYmd(day));
                  setOpen(false);
                }}
                components={{ Day: ClickableDay }}
              />
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
      <Button variant="ghost" size="sm" className={styles.step} aria-label={t('activities.workPlan.next')} onClick={() => onPage(1)}>
        <ChevronRightIcon size={14} />
      </Button>
    </div>
  );
}
